import { Circle, util as fabricUtil } from 'fabric';
import { hasSelfIntersection } from './PolygonTool';
import { isCoarsePointer } from '../../../hooks/useHasCoarsePointer';

// Vertex-handle radii. Touch pointers get a larger hit target so points are
// actually grabbable with a finger (#747); the mouse keeps the compact dot.
export const VERTEX_HANDLE_RADIUS = 6;
export const VERTEX_HANDLE_RADIUS_TOUCH = 11;

/**
 * Convert polygon local point to world (canvas) coordinates.
 * Uses polygon's full transform matrix (position, rotation, scale).
 *
 * @param {fabric.Polygon} polygon
 * @param {{x: number, y: number}} localPoint - Point in polygon local space
 * @returns {{x: number, y: number}} World (canvas) coordinates
 */
export function localToWorld(polygon, localPoint) {
  const matrix = polygon.calcTransformMatrix();
  // polygon.points are relative to bounding-box top-left (0,0),
  // but calcTransformMatrix() uses the object center as local origin.
  // Subtract pathOffset to convert from bbox-relative to center-relative.
  const centered = {
    x: localPoint.x - (polygon.pathOffset?.x ?? 0),
    y: localPoint.y - (polygon.pathOffset?.y ?? 0),
  };
  return fabricUtil.transformPoint(centered, matrix);
}

/**
 * Convert world (canvas) point to polygon local coordinates.
 * Inverse of localToWorld.
 *
 * @param {fabric.Polygon} polygon
 * @param {{x: number, y: number}} worldPoint - Point in canvas space
 * @returns {{x: number, y: number}} Local (polygon) coordinates
 */
export function worldToLocal(polygon, worldPoint) {
  const matrix = polygon.calcTransformMatrix();
  const invMatrix = fabricUtil.invertTransform(matrix);
  const centered = fabricUtil.transformPoint(worldPoint, invMatrix);
  // Add pathOffset back to get bbox-relative (polygon.points) coordinates.
  return {
    x: centered.x + (polygon.pathOffset?.x ?? 0),
    y: centered.y + (polygon.pathOffset?.y ?? 0),
  };
}

/**
 * Create a Fabric.js Circle to serve as a vertex handle.
 *
 * @param {number} worldX - X position in world coordinates
 * @param {number} worldY - Y position in world coordinates
 * @param {number} index - Vertex index in polygon.points
 * @returns {fabric.Circle}
 */
export function createVertexHandle(worldX, worldY, index) {
  return new Circle({
    left: worldX,
    top: worldY,
    radius: isCoarsePointer() ? VERTEX_HANDLE_RADIUS_TOUCH : VERTEX_HANDLE_RADIUS,
    fill: '#10B981',
    stroke: '#FFFFFF',
    strokeWidth: 2,
    originX: 'center',
    originY: 'center',
    hasBorders: false,
    hasControls: false,
    selectable: true,
    evented: true,
    hoverCursor: 'grab',
    moveCursor: 'grabbing',
    data: { type: 'vertex-handle', vertexIndex: index },
  });
}

/**
 * Update polygon.points[index] from a world-coordinate position.
 * Returns new points array, or null if the change would create a self-intersection.
 *
 * @param {fabric.Polygon} polygon
 * @param {number} index - Vertex index to update
 * @param {{x: number, y: number}} worldPoint - New position in world coordinates
 * @returns {Array<{x: number, y: number}>|null} New points array, or null if invalid
 */
export function updatePolygonPoint(polygon, index, worldPoint) {
  const localPoint = worldToLocal(polygon, worldPoint);
  const newPoints = polygon.points.map((p, i) =>
    i === index ? { x: localPoint.x, y: localPoint.y } : { x: p.x, y: p.y }
  );

  if (hasSelfIntersection(newPoints)) {
    return null;
  }

  return newPoints;
}

/**
 * Reposition all handles to match the current polygon vertex world positions.
 * Call this after polygon.points changes (e.g., undo/redo or properties panel edit).
 *
 * @param {fabric.Polygon} polygon
 * @param {fabric.Circle[]} handles
 */
export function positionHandlesFromPolygon(polygon, handles) {
  polygon.points.forEach((localPoint, i) => {
    if (handles[i]) {
      const worldPoint = localToWorld(polygon, localPoint);
      handles[i].set({ left: worldPoint.x, top: worldPoint.y });
      handles[i].setCoords();
    }
  });
}

/**
 * Check if a Fabric.js object is a vertex handle (transient, non-persistent).
 *
 * @param {fabric.Object} obj
 * @returns {boolean}
 */
export function isVertexHandle(obj) {
  return obj?.data?.type === 'vertex-handle';
}
